import { type JSX } from 'react';
import { useLocation } from 'react-router';

import { DocumentApi } from 'Api/Document/DocumentApi';
import { ClientTPRMApi } from 'Api/TPRM/TPRMApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { getAllOrganizationsWithBasicData } from 'Api/UcrmTprm/UcrmTprmApi';
import { Link } from 'Components/Buttons/Buttons';
import { FoldersTable, FoldersTableProps } from 'Components/Folders/FoldersTable/FoldersTable';
import { PageDataLoader } from 'Components/PageDataLoader/PageDataLoader';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { TPRM_FOLDERS_LISTING, UCRM_FOLDERS_LISTING } from 'Config/PathTemplates';
import { SEARCH_PARAM_FOLDER_PRESELECTED_ORGANIZATION_ID, getCreateFolderUrl } from 'Helpers/URLBuilder/URLBuilder';
import { FolderBreadcrumbSource, FolderVersion } from 'Models/Folders';
import { OrganizationBase } from 'Models/Organization';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

export interface FoldersListingLoaderProps {
    documentApi: DocumentApi;
    module: DueDiligenceModule;
    moduleApi: ClientTPRMApi | ClientUcrmApi;
}

export const FoldersListingLoader = (props: FoldersListingLoaderProps): JSX.Element => {
    return <PageDataLoader path={props.module === ProductModule.UCRM ? UCRM_FOLDERS_LISTING : TPRM_FOLDERS_LISTING} fetchData={() => Promise.all([props.moduleApi.getAllCurrentFolders(), props.moduleApi.getFolderTypes(), getAllOrganizationsWithBasicData(props.moduleApi)])} renderForm={(result) => <FoldersListing documentApi={props.documentApi} folders={result[0].data} folderTypes={result[1].data.type_set} module={props.module} organizations={result[2].data} />} />;
};

interface FoldersListingProps {
    documentApi: DocumentApi;
    folders: FolderVersion[];
    folderTypes: string[];
    module: DueDiligenceModule;
    organizations: OrganizationBase[];
}

/**
 * Lists the current version of every folder across all of the module's organizations (TPRM third parties or UCRM businesses). Clicking on a folder name takes the user to the Manage Folder page.
 */
const FoldersListing = (props: FoldersListingProps): JSX.Element => {
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const initialOrganizationByWhichToFilter = query.get(SEARCH_PARAM_FOLDER_PRESELECTED_ORGANIZATION_ID) ?? undefined;

    const foldersListingProps: FoldersTableProps = {
        createButtonVariant: 'primaryButton',
        createFolderOrigin: { source: FolderBreadcrumbSource.FOLDERS_LISTING, module: props.module },
        documentApi: props.documentApi,
        folders: props.folders,
        folderTypes: props.folderTypes,
        module: props.module,
        organizations: props.organizations,
        preselectedOrganizationIdForFilter: initialOrganizationByWhichToFilter,
    };

    return (
        <PageLayoutDefault
            headerTitle={props.module === ProductModule.UCRM ? 'Business Folders' : 'Third-Party Folders'}
            headerDescription={props.module === ProductModule.UCRM ? 'Within versioned folders, manage documents related to businesses. Optionally, the folders have effective dates and expiration dates.' : 'Within versioned folders, manage documents related to third parties. Optionally, the folders have effective dates and expiration dates.'}
            headerButtons={
                <Link variant="primaryButton" to={getCreateFolderUrl({ source: FolderBreadcrumbSource.FOLDERS_LISTING, module: props.module })} fontAwesomeImage={ICON_ADD_CREATE}>
                    Create Folder
                </Link>
            }
            body={[
                {
                    content: <FoldersTable {...foldersListingProps} />,
                },
            ]}
        />
    );
};
